import './process-performance.css'

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Input, Modal, PageHeader, Spinner, Text } from '../../../Components'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { AllUnitsDailyOverviewChart } from './AllUnitsDailyOverviewChart'
import { AnomalyBanner } from './AnomalyBanner'
import { DailyPerformanceComboChart } from './DailyPerformanceComboChart'
import { DrillDown } from './DrillDown'
import { ProcessCountChart } from './ProcessCountChart'
import { ProcessStateChart } from './ProcessStateChart'
import { ProcessTimeScatter } from './ProcessTimeScatter'
import { ServerStatusPanel } from './ServerStatusPanel'
import { TimeSeriesChart } from './TimeSeriesChart'
import { UnitAvgBarChart } from './UnitAvgBarChart'
import { UnitStatsTable } from './UnitStatsTable'
import { getPresetDates, PRESETS } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

const TABS = [
  { key: 'overview', label: t.panoramica, icon: 'pi-chart-pie' },
  { key: 'units', label: t.performanceUnitTab, icon: 'pi-box' },
  { key: 'trends', label: t.trendAnalisi, icon: 'pi-chart-line' }
]

/**
 * Dashboard monitoraggio performance con tre tab (panoramica, unit, trend).
 * @returns {JSX.Element} Pagina performance monitor
 */
export function PerformanceDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [activePreset, setActivePreset] = useState('24h')
  const [dateRange, setDateRange] = useState(() => getPresetDates('24h'))
  const [selectedUnits, setSelectedUnits] = useState([])
  const [drillDownUnit, setDrillDownUnit] = useState(null)
  const [isBackfillLoading, setIsBackfillLoading] = useState(false)
  const [backfillResult, setBackfillResult] = useState(null)
  const [isBackfillModalOpen, setIsBackfillModalOpen] = useState(false)
  const [backfillHours, setBackfillHours] = useState(null)
  const [backfillStartDate, setBackfillStartDate] = useState(null)
  const [backfillEndDate, setBackfillEndDate] = useState(null)
  const [backfillMode, setBackfillMode] = useState('preset')
  const [customStartDate, setCustomStartDate] = useState(() => getPresetDates('24h').startDate)
  const [customEndDate, setCustomEndDate] = useState(() => getPresetDates('24h').endDate)

  const { data: overview = {}, isSuccess: overviewLoaded } = useCQuery([
    'unitPerformanceOverview',
    dateRange.startDate,
    dateRange.endDate
  ])

  const { data: unitStatsForBar = [] } = useCQuery([
    'unitPerformanceStats',
    dateRange.startDate,
    dateRange.endDate
  ])

  const processStepsStatsForBar = useMemo(
    () =>
      unitStatsForBar.map((s) => ({
        unit: s.unit,
        executionTime: (s.avg || 0) / 1000
      })),
    [unitStatsForBar]
  )

  const handlePreset = useCallback((key) => {
    setActivePreset(key)
    const range = getPresetDates(key)
    setDateRange(range)
    setCustomStartDate(range.startDate)
    setCustomEndDate(range.endDate)
  }, [])

  const handleSelectUnit = useCallback((unit) => {
    setSelectedUnits((prev) => {
      if (prev.includes(unit)) return prev.filter((u) => u !== unit)
      if (prev.length >= 5) return [...prev.slice(1), unit]
      return [...prev, unit]
    })
  }, [])

  const handleDrillDown = useCallback((unit) => {
    setDrillDownUnit(unit)
  }, [])

  const handleTableAction = useCallback(
    (unit, action) => {
      if (action === 'chart') handleSelectUnit(unit)
      if (action === 'details') handleDrillDown(unit)
    },
    [handleSelectUnit, handleDrillDown]
  )

  const handleStateClick = useCallback(
    (stateValue) => {
      if (stateValue === 5) {
        navigate('/process/errorStats')
      }
    },
    [navigate]
  )

  const hoursBack = useMemo(() => {
    const diffMs = dateRange.endDate.getTime() - dateRange.startDate.getTime()
    const hours = Math.ceil(diffMs / (60 * 60 * 1000))
    return Math.max(1, hours)
  }, [dateRange])

  const handleBackfill = useCallback(
    async (customHoursBack, startDate, endDate) => {
      try {
        setIsBackfillLoading(true)
        setBackfillResult(null)
        const hoursToUse = customHoursBack || hoursBack
        const query =
          startDate && endDate
            ? { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
            : { hoursBack: hoursToUse }
        const res = await FC.service('info').get('unitPerformanceRefreshCache', { query })
        setBackfillResult(res)
        invalidateQuery([
          'unitPerformanceStats',
          'unitPerformanceTimeSeries',
          'unitPerformanceProcessDaily',
          'unitPerformanceAnomalies',
          'unitPerformanceOverview'
        ])
      } catch (error) {
        setBackfillResult({ ok: false, error: error?.message || String(error) })
      } finally {
        setIsBackfillLoading(false)
      }
    },
    [hoursBack]
  )

  const openBackfillModal = useCallback(() => {
    setBackfillHours(hoursBack)
    setBackfillStartDate(dateRange.startDate)
    setBackfillEndDate(dateRange.endDate)
    setIsBackfillModalOpen(true)
  }, [hoursBack, dateRange])

  const applyBackfill = useCallback(async () => {
    if (backfillMode === 'period' && backfillStartDate && backfillEndDate) {
      await handleBackfill(null, backfillStartDate, backfillEndDate)
    } else {
      const parsed = Math.max(1, parseInt(backfillHours || hoursBack))
      await handleBackfill(parsed)
    }
    setIsBackfillModalOpen(false)
  }, [backfillMode, backfillHours, hoursBack, handleBackfill, backfillStartDate, backfillEndDate])

  const applyCustomStartDate = useCallback(
    (val) => {
      setCustomStartDate(val)
      if (val && customEndDate) {
        setActivePreset('custom')
        setDateRange({ startDate: val, endDate: customEndDate })
      }
    },
    [customEndDate]
  )

  const applyCustomEndDate = useCallback(
    (val) => {
      setCustomEndDate(val)
      if (customStartDate && val) {
        setActivePreset('custom')
        setDateRange({ startDate: customStartDate, endDate: val })
      }
    },
    [customStartDate]
  )

  const cacheSubtitle = overviewLoaded
    ? `${t.cache}: ${hoursBack}h (${overview.totalProcessCount ?? 0} ${t.processes} · ${unitStatsForBar.length} ${t.unit})`
    : t.caricamentoDatiCache

  return (
    <div className="perf-dashboard">
      <PageHeader
        title={t.performanceMonitor}
        subtitle={cacheSubtitle}
        actions={
          <>
            <button type="button" className="nbtn" onClick={() => navigate('/conversations/list')}>
              <i className="pi pi-comments perf-icon-sm" />
              {t.listaConversazioni}
            </button>
            <button type="button" className="nbtn" onClick={() => navigate('/process/errorStats')}>
              <i className="pi pi-exclamation-triangle perf-icon-sm" />
              {t.statisticheErrori}
            </button>
          </>
        }
      />

      <div className="ncard perf-period-card">
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-calendar" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.periodo}</h3>
            <div className="ncard__sub">{t.selezionaRange}</div>
          </div>
        </div>
        <div className="ncard__body">
          <div className="perf-period-card__row">
            <div className="perf-period-card__fields">
              <div className="nfield nora-date-field" style={{ minWidth: 200 }}>
                <label>{t.dataInizio}</label>
                <Input
                  id="customStartDate"
                  date
                  showIcon
                  value={customStartDate}
                  style={{ width: '100%' }}
                  onChange={({ customStartDate: val }) => applyCustomStartDate(val)}
                />
              </div>
              <div className="nfield nora-date-field" style={{ minWidth: 200 }}>
                <label>{t.dataFine}</label>
                <Input
                  id="customEndDate"
                  date
                  showIcon
                  value={customEndDate}
                  style={{ width: '100%' }}
                  onChange={({ customEndDate: val }) => applyCustomEndDate(val)}
                />
              </div>
            </div>
            <div className="perf-period-card__presets">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`npill ${activePreset === p.key ? 'is-active' : ''}`}
                  onClick={() => handlePreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {backfillResult && (
        <div
          className={`nora-banner ${backfillResult.ok ? 'nora-banner--backfill-ok' : 'nora-banner--backfill-error'}`}
        >
          <div className="nora-banner__text">
            <div className="nora-banner__title">
              {backfillResult.ok ? t.cacheAggiornata : t.erroreBackfillCache}
            </div>
            <div className="nora-banner__detail">
              {backfillResult.ok
                ? `unitStatsHourly ${backfillResult.upserted ?? 0} bucket, processOverviewHourly ${backfillResult.upsertedOverview ?? 0} bucket (hoursBack=${backfillResult.hoursBack})`
                : backfillResult.error || t.erroreSconosciuto}
            </div>
          </div>
        </div>
      )}

      <div className="nora-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`nora-tab ${activeTab === t.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            <i className={`pi ${t.icon} perf-icon-sm`} />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="perf-tab-content">
          <AnomalyBanner />
          {overviewLoaded && (overview.cacheHours === 0 || overview.totalProcessCount === 0) && (
            <div className="nora-banner nora-banner--warning">
              <div className="nora-banner__ico nora-banner__ico--warning">
                <i className="pi pi-info-circle perf-icon-lg" />
              </div>
              <div className="nora-banner__text">
                <div className="nora-banner__detail">{t.nessunDatoCache}</div>
              </div>
            </div>
          )}
          <div className="ngrid-2 perf-overview-grid">
            <ProcessStateChart
              stateCounts={overview.stateCounts}
              totalCount={overview.totalProcessCount}
              onStateClick={handleStateClick}
            />
            <ServerStatusPanel gianpietroStats={[]} channels={overview.channels || []} />
          </div>
          <ProcessCountChart
            hourlyFromCache={overview.hourlyCreated}
            totalFromCache={overview.totalProcessCount}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </div>
      )}

      {activeTab === 'units' && (
        <div className="perf-tab-content">
          <UnitStatsTable
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onSelectUnit={handleTableAction}
          />
          <UnitAvgBarChart processStepsStats={processStepsStatsForBar} />
          <div className="perf-units-section">
            <p className="perf-units-hint">{t.selezioneGrafico}</p>
            <div className="perf-units-chips">
              {selectedUnits.map((unit) => (
                <span key={unit} className="nchip perf-chip--unit">
                  {unit}
                  <button
                    type="button"
                    className="nchip__x"
                    onClick={() => handleSelectUnit(unit)}
                    aria-label={`${t.rimuovi} ${unit}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <TimeSeriesChart
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              selectedUnits={selectedUnits}
            />
            <DailyPerformanceComboChart
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              selectedUnits={selectedUnits}
            />
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="perf-tab-content">
          <AllUnitsDailyOverviewChart startDate={dateRange.startDate} endDate={dateRange.endDate} />
          <ProcessTimeScatter
            processes={overview.scatterProcesses || []}
            cacheHint={overview.scatterTruncated ? t.pfScatterCacheHintLong : t.pfScatterCacheHint}
          />
        </div>
      )}

      {drillDownUnit && (
        <DrillDown
          unit={drillDownUnit}
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onClose={() => setDrillDownUnit(null)}
        />
      )}

      <button
        type="button"
        className="nora-fab nora-fab--fixed"
        onClick={openBackfillModal}
        disabled={isBackfillLoading}
      >
        {isBackfillLoading ? (
          <>
            <Spinner style={{ width: 16, height: 16, marginTop: 0 }} />
            {t.aggiornamentoCache}
          </>
        ) : (
          <>
            <i className="pi pi-refresh perf-icon-sm" />
            {t.backfillCache}
          </>
        )}
      </button>

      <Modal
        visible={isBackfillModalOpen}
        onHide={() => setIsBackfillModalOpen(false)}
        header={t.backfillCacheStatistiche}
        className="nora-dialog"
        contentStyle={{ padding: 0 }}
        style={{ width: 'min(560px, 92vw)' }}
      >
        <div className="nora-dialog__body--compact">
          <Text value={t.precomputaDescrizione} size={13} style={{ color: 'var(--ink-500)' }} />
        </div>
        <div className="nora-dialog__tabs">
          {[
            { key: 'preset', label: t.preset, icon: 'pi-sliders-h' },
            { key: 'period', label: t.periodo, icon: 'pi-calendar' },
            { key: 'custom', label: t.oreCustom, icon: 'pi-clock' }
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`nora-dialog__tab ${backfillMode === tab.key ? 'is-active' : ''}`}
              onClick={() => setBackfillMode(tab.key)}
            >
              <i className={`pi ${tab.icon}`} />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="nora-dialog__body">
          {backfillMode === 'preset' && (
            <div className="perf-backfill-modal__presets">
              {[1, 6, 24, 24 * 7, 24 * 30].map((h) => (
                <button
                  key={h}
                  type="button"
                  className={`npill ${Number(backfillHours) === h ? 'is-active' : ''}`}
                  onClick={() => setBackfillHours(h)}
                >
                  {h === 1 ? '1h' : h === 6 ? '6h' : h === 24 ? '24h' : h === 168 ? '7g' : '30g'}
                </button>
              ))}
              <button
                type="button"
                className={`npill ${Number(backfillHours) === Number(hoursBack) ? 'is-active' : ''}`}
                onClick={() => setBackfillHours(hoursBack)}
              >
                {t.rangePagina} ({hoursBack}h)
              </button>
            </div>
          )}
          {backfillMode === 'period' && (
            <div className="perf-backfill-modal__dates">
              <Input
                id="backfillStartDate"
                label={t.dataInizio}
                date
                value={backfillStartDate}
                style={{ width: '100%' }}
                onChange={({ backfillStartDate: val }) => setBackfillStartDate(val)}
              />
              <Input
                id="backfillEndDate"
                label={t.dataFine}
                date
                value={backfillEndDate}
                style={{ width: '100%' }}
                onChange={({ backfillEndDate: val }) => setBackfillEndDate(val)}
              />
            </div>
          )}
          {backfillMode === 'custom' && (
            <div className="perf-backfill-modal__custom-hours-row">
              <Input
                id="backfillHours"
                label={t.oreDaRicalcolare}
                value={backfillHours}
                style={{ width: '100%' }}
                onChange={({ backfillHours: val }) => setBackfillHours(val)}
              />
            </div>
          )}
        </div>
        <div className="nora-dialog__body nora-dialog__footer">
          <button type="button" className="nbtn" onClick={() => setIsBackfillModalOpen(false)}>
            {t.annulla}
          </button>
          <button
            type="button"
            className="nbtn nbtn--primary"
            onClick={applyBackfill}
            disabled={isBackfillLoading}
          >
            {isBackfillLoading ? t.esecuzioneInCorso : t.eseguiBackfill}
          </button>
        </div>
      </Modal>
    </div>
  )
}
